/**
 * Session identity, shared by every platform's recorder.
 *
 * A session id is a blob path segment, the SQL index key, and the value pasted
 * into a Halo ITSM ticket, so both the charset contract and the entropy source
 * have to be identical on web and mobile or the same id is accepted by one
 * client and rejected by ingest from the other.
 */

/** Matches `ReplayEnvelopeValidator.MaxSessionIdLength`. */
export const RYN_REPLAY_SESSION_ID_MAX_LENGTH = 120;

/**
 * Mirrors the ingest validator's safe-token contract
 * (`ReplayEnvelopeValidator.IsSafeToken`): the charset `[A-Za-z0-9._-]` with no
 * '..', no leading dot and no trailing dot. Each dot must sit between two
 * non-dot runs, which is what forbids '..' and a dot at either end.
 */
export const RYN_REPLAY_SAFE_SESSION_ID =
  /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/;

/**
 * True when a value read back from storage may be reused as a session id. A
 * tampered or truncated value must be treated as absent rather than carried
 * into a blob path ingest would then reject.
 */
export const isSafeRynReplaySessionId = (
  value: string | null | undefined
): value is string =>
  !!value &&
  value.length <= RYN_REPLAY_SESSION_ID_MAX_LENGTH &&
  RYN_REPLAY_SAFE_SESSION_ID.test(value);

/**
 * Mint a session id.
 *
 * The ladder is deliberate, strongest first, and it must not throw: this runs
 * during recorder construction, so a throw here takes out the provider graph of
 * every app that opted in.
 *
 * `randomUUID` is preferred because the index, the blob paths, and every row
 * already written assume that shape. It is only exposed in a secure context,
 * which is why `getRandomValues` is the second rung and not the last resort.
 *
 * `Math.random` is the last rung only because a context with no crypto at all
 * still needs an id. It is not an acceptable substitute: V8 implements it as a
 * seeded xorshift128+, so its output is predictable from prior samples, and
 * pairing it with `Date.now()` pairs a guessable value with a guessable value.
 * Guessable ids matter little while the player's read gate holds and matter a
 * great deal the moment anything reaches the blobs directly, which is the whole
 * point of not stopping at this rung.
 */
export const createRynReplaySessionId = (): string => {
  const cryptoRef = globalThis.crypto;

  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }

  if (typeof cryptoRef?.getRandomValues === 'function') {
    const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

/**
 * Where a platform keeps the id between boots. Both methods are allowed to fail
 * silently — a blocked or absent store degrades to a fresh id per launch, which
 * is a shorter session, not a broken one.
 */
export interface IRynReplaySessionIdStore {
  read(): string | undefined;
  write(sessionId: string): void;
}

/**
 * Reuse the stored id when one is present and still safe, otherwise mint and
 * store a fresh one.
 *
 * Without this the id was minted on every app boot and thrown away, so a full
 * page load — and critically the Auth0 login redirect — started a brand new
 * session: one real user journey through web-software-security fragmented into
 * 8 sessions, with the anonymous pre-login half never linked to the identified
 * post-login half (#30109).
 *
 * What counts as "the same visit" is the STORE's decision, not this function's,
 * and the two platforms answer it differently on purpose: web uses
 * sessionStorage, whose per-tab lifetime is exactly one visit, while mobile has
 * no redirect to survive and treats a launch as a session. Keeping the lifetime
 * in the store is also what keeps this free of a durable per-device identifier,
 * so it opens no new privacy surface; cross-visit stitching would need one and
 * is a separate decision (#30109 part two).
 */
export const resolveRynReplaySessionId = (
  store?: IRynReplaySessionIdStore
): string => {
  let persisted: string | undefined;
  try {
    persisted = store?.read();
  } catch {
    persisted = undefined;
  }
  if (isSafeRynReplaySessionId(persisted)) {
    return persisted;
  }

  const created = createRynReplaySessionId();
  try {
    store?.write(created);
  } catch {
    // Best-effort. The id is already live for this session; it just will not
    // survive the next boot.
  }
  return created;
};
